import { useState, useEffect } from 'react';
import { classAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import {
  RiAddLine, RiEditLine, RiDeleteBinLine, RiBookOpenLine,
  RiCloseLine, RiLoaderLine, RiCalendarLine,
} from 'react-icons/ri';

const ClassManagement = () => {
  const [classes, setClasses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchClasses(); }, []);

  const fetchClasses = async () => {
    try {
      const response = await classAPI.getAllClasses();
      setClasses(response.data.classes || []);
    } catch {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await classAPI.updateClass(editingId, formData);
        toast.success('Class updated successfully');
      } else {
        await classAPI.createClass(formData);
        toast.success('Class created successfully');
      }
      setFormData({ name: '', description: '' });
      setShowForm(false);
      setEditingId(null);
      fetchClasses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will delete all associated subjects and chapters.')) return;
    try {
      await classAPI.deleteClass(id);
      toast.success('Class deleted');
      fetchClasses();
    } catch {
      toast.error('Failed to delete class');
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ name: '', description: '' });
    setShowForm(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 spinner" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Classes</h1>
          <p className="text-sm text-slate-500 mt-0.5">{classes.length} class{classes.length !== 1 ? 'es' : ''}</p>
        </div>
        <button onClick={openCreate} className="btn-primary whitespace-nowrap">
          <RiAddLine className="w-4 h-4" /> Add Class
        </button>
      </div>

      {/* Form panel */}
      {showForm && (
        <div className="card p-6 mb-6 animate-fade-in-up border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-slate-800">
              {editingId ? 'Edit Class' : 'Create New Class'}
            </h2>
            <button onClick={() => setShowForm(false)} className="btn-icon btn-ghost text-slate-400">
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
            <div>
              <label className="label">Class Name *</label>
              <input className="input" type="text" placeholder="e.g. Class 10" value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea className="input resize-none" placeholder="Optional description" value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} />
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving} className="btn-primary flex-1">
                {saving ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Saving…</> : editingId ? 'Update Class' : 'Create Class'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Grid */}
      {classes.length === 0 ? (
        <div className="card p-12 text-center">
          <RiBookOpenLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No classes yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first class to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {classes.map(cls => (
            <div key={cls._id} className="card-hover p-5 animate-fade-in-up group">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <RiBookOpenLine className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => { setEditingId(cls._id); setFormData({ name: cls.name, description: cls.description || '' }); setShowForm(true); }}
                    className="btn-icon btn-ghost text-blue-600 hover:bg-blue-50"
                  >
                    <RiEditLine className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(cls._id)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50">
                    <RiDeleteBinLine className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-semibold text-slate-800 mb-1">{cls.name}</h3>
              <p className="text-sm text-slate-500 line-clamp-2 mb-3">{cls.description || 'No description'}</p>
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <RiCalendarLine className="w-3 h-3" />
                {new Date(cls.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassManagement;
